import React, { useState } from 'react';
import { setupTenant } from '../services/api';
import { T } from '../styles/ds';

function SetupWizard({ onComplete }) {
  const [setupToken] = useState(() => {
    const _hashToken = window.location.hash.startsWith('#token=')
      ? window.location.hash.slice(7)
      : '';
    const token = _hashToken || new URLSearchParams(window.location.search).get('token') || '';
    if (_hashToken) {
      history.replaceState(null, '', window.location.pathname);
    }
    return token;
  });
  const [shopName, setShopName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  // PH-43 — the shop's own customer return window (days after billing). Optional here: blank means
  // "decide later in Settings" and is NOT sent, so the backend never records a number nobody chose.
  const [customerReturnDays, setCustomerReturnDays] = useState('');
  // PH-71 — a brand-new shop goes straight to per-person staff login: this becomes the shop's
  // first Admin person (name + PIN), replacing the old three-role-PIN trio.
  const [ownerName, setOwnerName] = useState('');
  const [ownerPin, setOwnerPin] = useState('');
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');

  const validate = () => {
    const errs = {};
    if (!shopName.trim()) errs.shopName = 'दुकान का नाम डालें / Shop name required';

    if (!phone.trim()) {
      errs.recovery_phone = 'रिकवरी फोन जरूरी है / Recovery phone required';
    } else if (!/^[0-9]{10}$/.test(phone.trim())) {
      errs.recovery_phone = '10 अंक का नंबर डालें / Enter 10-digit number';
    }
    if (!email.trim()) {
      errs.recovery_email = 'रिकवरी ईमेल जरूरी है / Recovery email required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errs.recovery_email = 'सही ईमेल डालें / Enter valid email';
    }

    if (customerReturnDays !== '' && Number(customerReturnDays) > 3650) {
      errs.customer_return_days = 'ज़्यादा से ज़्यादा 3650 दिन / At most 3650 days';
    }

    if (!ownerName.trim()) {
      errs.ownerName = 'आपका नाम डालें / Your name is required';
    }
    if (!ownerPin) {
      errs.ownerPin = 'PIN required';
    } else if (!/^\d{6,12}$/.test(ownerPin)) {
      errs.ownerPin = '6–12 अंक का PIN / 6–12 digit PIN';
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitted(true);
    setApiError('');
    try {
      await setupTenant({
        shop_name: shopName.trim(),
        owner_name: ownerName.trim(),
        owner_pin: ownerPin,
        primary_color: T.ink,
        recovery_phone: '+91' + phone.trim(),
        recovery_email: email.trim(),
        ...(customerReturnDays !== '' ? { customer_return_days: Number(customerReturnDays) } : {}),
      }, setupToken);
      // PH-71 — no client-side PIN hash is cached any more (the old three-role-PIN trio's hashes
      // used to be stashed in `tenantConfig.pins` for the demo/offline PIN-check path — staff
      // login always verifies server-side, so there is nothing to cache here).
      const config = {
        shopName: shopName.trim(),
        recoveryPhone: '+91' + phone.trim(),
        recoveryEmail: email.trim(),
        isConfigured: true,
        staffLoginEnabled: true,
      };
      localStorage.setItem('tenantConfig', JSON.stringify(config));
      setTimeout(() => onComplete(config), 800);
    } catch (err) {
      setSubmitted(false);
      const detail = err.response?.data?.detail;
      setApiError(detail || 'Setup failed — please try again');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>

      <div style={{ marginBottom: 28, textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🏪</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: T.ink }}>पहली बार सेटअप</div>
        <div style={{ fontSize: 13, color: T.mute, marginTop: 4 }}>First-time setup — takes 30 seconds</div>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', width: '100%', maxWidth: 420 }}>
        <form onSubmit={handleSubmit}>

          {/* Shop name */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: T.sec, marginBottom: 6 }}>
              दुकान का नाम / Shop Name
            </label>
            <input
              autoFocus
              type="text"
              value={shopName}
              onChange={e => { setShopName(e.target.value); setErrors(p => ({ ...p, shopName: null })); }}
              placeholder="जैसे: Sharma Medical, Apollo Pharmacy..."
              style={{
                width: '100%', padding: '11px 14px', border: `2px solid ${errors.shopName ? T.red.text : T.border}`,
                borderRadius: 8, fontSize: 15, boxSizing: 'border-box', outline: 'none',
              }}
            />
            {errors.shopName && <div style={{ color: T.red.text, fontSize: 12, marginTop: 4 }}>{errors.shopName}</div>}
          </div>

          {/* Recovery contact */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: T.sec, marginBottom: 4 }}>
              PIN रिकवरी / PIN Recovery
            </label>
            <div style={{ fontSize: 12, color: T.mute, marginBottom: 10 }}>
              अगर PIN भूल जाएँ तो इस पर OTP भेजा जाएगा — Both required *
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: T.sec, marginBottom: 4 }}>📱 फोन* / Phone*</div>
                <div style={{ display: 'flex', border: `2px solid ${errors.recovery_phone ? T.red.text : T.border}`, borderRadius: 8, overflow: 'hidden' }}>
                  <span style={{ padding: '10px 10px', background: T.bg2, fontSize: 14, color: T.sec, borderRight: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={phone}
                    onChange={e => { setPhone(e.target.value.replace(/\D/g, '')); setErrors(p => ({ ...p, recovery_phone: null })); }}
                    placeholder="98765 43210"
                    style={{ flex: 1, padding: '10px 10px', border: 'none', fontSize: 14, outline: 'none', background: '#fff' }}
                  />
                </div>
                {errors.recovery_phone && <div style={{ color: T.red.text, fontSize: 11, marginTop: 3 }}>{errors.recovery_phone}</div>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: T.sec, marginBottom: 4 }}>✉️ ईमेल* / Email*</div>
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, recovery_email: null })); }}
                  placeholder="you@example.com"
                  style={{
                    width: '100%', padding: '10px 12px', border: `2px solid ${errors.recovery_email ? T.red.text : T.border}`,
                    borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none',
                  }}
                />
                {errors.recovery_email && <div style={{ color: T.red.text, fontSize: 11, marginTop: 3 }}>{errors.recovery_email}</div>}
              </div>
            </div>
          </div>

          {/* Customer return window (PH-43) — optional; blank = decide later in Settings */}
          <div style={{ marginBottom: 24 }}>
            <label htmlFor="customer-return-days" style={{ display: 'block', fontSize: 13, fontWeight: 700, color: T.sec, marginBottom: 4 }}>
              ग्राहक वापसी की समय-सीमा / Customer return window
            </label>
            <div style={{ fontSize: 12, color: T.mute, marginBottom: 8 }}>
              बिल के कितने दिन बाद तक आप ग्राहक से माल वापस लेते हैं? खाली छोड़ें तो बाद में Settings में तय कर सकते हैं। /
              Days after billing within which you take stock back from a customer. Leave blank to decide later in Settings.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="customer-return-days"
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={customerReturnDays}
                onChange={e => { setCustomerReturnDays(e.target.value.replace(/\D/g, '')); setErrors(p => ({ ...p, customer_return_days: null })); }}
                placeholder="जैसे: 7"
                style={{
                  width: 120, padding: '10px 12px', border: `2px solid ${errors.customer_return_days ? T.red.text : T.border}`,
                  borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none',
                }}
              />
              <span style={{ fontSize: 13, color: T.sec }}>दिन / days</span>
            </div>
            {errors.customer_return_days && <div style={{ color: T.red.text, fontSize: 11, marginTop: 3 }}>{errors.customer_return_days}</div>}
          </div>

          {/* Owner — name + PIN (PH-71: replaces the old three-role-PIN trio) */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.sec, marginBottom: 12 }}>
              आपका नाम और PIN / Your name and PIN
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${T.ink}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                👑
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
                  मालिक <span style={{ color: T.mute, fontWeight: 400 }}>· Owner / Admin</span>
                </div>
                <input
                  type="text"
                  maxLength={50}
                  value={ownerName}
                  onChange={e => { setOwnerName(e.target.value); setErrors(p => ({ ...p, ownerName: null })); }}
                  placeholder="आपका नाम / Your name"
                  style={{
                    width: '100%', marginTop: 4, padding: '8px 12px',
                    border: `2px solid ${errors.ownerName ? T.red.text : T.border}`,
                    borderRadius: 6, fontSize: 15, outline: 'none', boxSizing: 'border-box',
                  }}
                />
                {errors.ownerName && <div style={{ color: T.red.text, fontSize: 11, marginTop: 2 }}>{errors.ownerName}</div>}
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={12}
                  value={ownerPin}
                  onChange={e => { setOwnerPin(e.target.value); setErrors(p => ({ ...p, ownerPin: null })); }}
                  placeholder="6–12 digit PIN"
                  style={{
                    width: '100%', marginTop: 8, padding: '8px 12px',
                    border: `2px solid ${errors.ownerPin ? T.red.text : T.border}`,
                    borderRadius: 6, fontSize: 15, letterSpacing: 4, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                {errors.ownerPin && <div style={{ color: T.red.text, fontSize: 11, marginTop: 2 }}>{errors.ownerPin}</div>}
              </div>
            </div>
            <div style={{ fontSize: 12, color: T.mute }}>
              बाद में Settings में और लोग जोड़ें / Add more people later in Settings
            </div>
          </div>

          {apiError && (
            <div style={{ color: T.red.text, fontSize: 13, marginTop: 12, padding: '8px 12px', background: '#fdecea', borderRadius: 6 }}>
              {apiError}
            </div>
          )}
          <button
            type="submit"
            disabled={submitted}
            style={{
              width: '100%', marginTop: 20, padding: '13px',
              background: submitted ? T.green.text : T.ink,
              color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 15, fontWeight: 700, cursor: submitted ? 'default' : 'pointer',
              transition: 'background 0.3s',
            }}
          >
            {submitted ? '✓ सेटअप पूरा / Setup Complete' : 'शुरू करें / Get Started →'}
          </button>
        </form>
      </div>

      <div style={{ marginTop: 16, fontSize: 12, color: T.mute, textAlign: 'center' }}>
        यह जानकारी सुरक्षित रहेगी / This information is stored securely
      </div>
    </div>
  );
}

export default SetupWizard;
